import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { WACategoryType } from "./WACategoryType";
import { graphql, useLazyLoadQuery } from "react-relay";
import {
  Features,
  useHasFeatureInTier,
} from "../../../../../useHasFeatureInTier";
import { useWidgetCategoriesQuery } from "./__generated__/useWidgetCategoriesQuery.graphql";
import { useMemo } from "react";
import { WACategoryKey } from "../WANavigation";
import { useHasFeatureFlag } from "@/src/useHasFeatureFlag";
import { FeatureFlags } from "@/src/config";

export function useWidgetCategories() {
  const intl_tokens = useIntlTokens();

  const { store } = useLazyLoadQuery<useWidgetCategoriesQuery>(
    graphql`
      query useWidgetCategoriesQuery {
        store {
          isPartOfOrg
          ...useHasFeatureInTier_Store
          defaultTodoCategory {
            id
            title
            icon
          }
        }
      }
    `,
    {}
  );

  const hasOperationsPlatform = useHasFeatureInTier(
    Features.OPERATIONS_PLATFORM,
    store
  );

  const hasEmployeeRole = useHasFeatureFlag(FeatureFlags.EmployeeRole);

  const hasOrg = store.isPartOfOrg;

  const hasFiles = useHasFeatureInTier(Features.FILES, store);

  const hqInfo: WACategoryType<WACategoryKey>[][] = useMemo(() => {
    if (hasOrg) {
      return [
        [
          {
            title: intl_tokens.HQInfo,
            icon: "org",
            color: "#000000",
            key: "WACategoryOrgInfo",
          },
        ],
      ];
    }
    return [];
  }, [hasOrg, intl_tokens.HQInfo]);

  const calendarCategory: WACategoryType<WACategoryKey>[] = [
    {
      title: intl_tokens.Calendar,
      icon: "calendar",
      color: "#5AC8FA",
      key: "WACategoryCalendar",
    },
  ];

  const fileCategory: WACategoryType<WACategoryKey>[] = hasFiles
    ? [
        {
          title: intl_tokens.Files,
          icon: "files" as const,
          color: "#000000",
          key: "WACategoryFile" as const,
        },
      ]
    : [];

  const signalsCategory: WACategoryType<WACategoryKey>[] = hasOrg
    ? [
        {
          title: intl_tokens.Signals,
          icon: "signal",
          color: "#1115ba",
          key: "WACategorySignals",
        },
      ]
    : [];

  const employeeRolesCategory: WACategoryType<WACategoryKey>[] = hasEmployeeRole
    ? [
        {
          title: intl_tokens.Lineup,
          icon: "employee-role",
          color: "#7B61FF",
          key: "WACategoryEmployeeRoles",
        },
      ]
    : [];

  if (!hasOperationsPlatform) {
    const categories: WACategoryType<WACategoryKey>[][] = [
      ...hqInfo,
      [
        {
          title: intl_tokens.Note,
          icon: "edit",
          color: "#A97AEC",
          key: "WACategoryNote",
        },
      ],
      fileCategory,
      calendarCategory,
      [
        {
          title: intl_tokens.Integrations,
          icon: "integration",
          color: "#6290EC",
          key: "WACategoryIntegrations",
        },
      ],
      [
        {
          title: intl_tokens.Weather,
          icon: "sun",
          color: "#F2A1A1",
          key: "WACategoryWeather",
        },
      ],
    ];
    return { categories: categories.filter((category) => category.length > 0) };
  }

  const defaultTodoCategory: WACategoryType<WACategoryKey>[] =
    store.defaultTodoCategory
      ? [
          {
            title: store.defaultTodoCategory.title,
            icon: "check",
            color: "#0066FF",
            key: "WACategoryDefaultTodoCategory",
          },
        ]
      : [];

  const categories: WACategoryType<WACategoryKey>[][] = [
    ...hqInfo,
    [
      {
        title: intl_tokens.CustomLists,
        icon: "todo-category",
        color: "#FF92E8",
        key: "WACategoryTodoCategories",
      },
      ...defaultTodoCategory,
    ],
    [
      {
        title: intl_tokens.TeamSchedule,
        icon: "team-schedule",
        color: "#A97AEC",
        key: "WACategoryTeamSchedule",
      },
      {
        title: intl_tokens.Checklists,
        icon: "routine",
        color: "#43DD87",
        key: "WACategoryChecklists",
      },
      ...calendarCategory,
    ],
    employeeRolesCategory,
    [
      {
        title: intl_tokens.KPIs,
        icon: "KPI",
        color: "#F06072",
        key: "WACategoryKPI",
      },
    ],
    [
      ...signalsCategory,
      {
        title: intl_tokens.Note,
        icon: "edit",
        color: "#F5A623",
        key: "WACategoryNote",
      },
      {
        title: intl_tokens.Weather,
        icon: "sun",
        color: "#F2A1A1",
        key: "WACategoryWeather",
      },

      ...fileCategory,

      {
        title: intl_tokens.Integrations,
        icon: "integration",
        color: "#6290EC",
        key: "WACategoryIntegrations",
      },
    ],
  ];
  return {
    categories: categories.filter((category) => category.length > 0),
    defaultTodoCategoryId: store.defaultTodoCategory?.id,
  };
}
